import React, { useEffect, useState } from "react";
import { Heart, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LoveMessages() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setItems(data);
    } catch (e) { console.error('error in LoveMessages.jsx', e); }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!content.trim()) return;
    try {
      await api.post("/messages", { content });
      setContent("");
      setOpen(false);
      load();
      toast.success("Mot d'amour ajouté ♡");
    } catch {
      toast.error("Échec de l'ajout");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/messages/${id}`);
      setItems((s) => s.filter((x) => x.id !== id));
    } catch (e) { console.error('error in LoveMessages.jsx', e); }
  };

  return (
    <section id="mots" data-testid="messages-section" className="px-6 md:px-12 lg:px-24 py-20 lg:py-28 relative">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-accent text-3xl text-rose-300">pour toi</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-rose-50 mt-2">Mots d'amour</h2>
            <p className="text-rose-200/70 mt-3 max-w-xl">De petites pensées, écrites pour toi.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-message-dialog"
                className="bg-rose-600 hover:bg-rose-500 text-white rounded-full px-6 h-12"
              >
                <Plus className="w-4 h-4 mr-2" />
                Écrire un mot
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Un nouveau mot</DialogTitle>
              </DialogHeader>
              <Textarea
                data-testid="message-content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Amor, ..."
                rows={6}
                className="bg-[#0F0407] border-rose-900/40"
              />
              <DialogFooter>
                <Button
                  data-testid="message-submit-button"
                  onClick={submit}
                  className="bg-rose-600 hover:bg-rose-500 text-white rounded-full"
                >
                  Sauvegarder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {items.length === 0 ? (
          <div className="romantic-card rounded-2xl p-16 text-center">
            <Heart className="w-10 h-10 text-rose-400 mx-auto" fill="currentColor" />
            <p className="font-accent text-3xl text-rose-300 mt-4">Écris ton premier mot doux</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((m) => (
              <div key={m.id} className="romantic-card rounded-xl p-6 group relative transition-all hover:-translate-y-1">
                <Heart className="w-5 h-5 text-rose-500 mb-3" fill="currentColor" />
                <p className="font-accent text-2xl text-rose-100 leading-snug whitespace-pre-wrap">{m.content}</p>
                <button
                  data-testid={`message-delete-${m.id}`}
                  onClick={() => remove(m.id)}
                  className="absolute top-3 right-3 text-rose-200/30 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
